"use strict";
/* globals module */
/**
 * @module blogPostStatusClient
 * @description  Blog Post Status Client provides an easy way to consume  Blogs REST API end-points. In order to obtain needed routes `blogPostStatusClient` uses `blogPostStatusRoute`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
var common_1 = require("../../common");
var httpApi_1 = require("../../httpApi");
var _1 = require("./");
var BlogPostStatusClient = /** @class */ (function () {
    function BlogPostStatusClient(blogPostStatusRoute, apiClient) {
        this.blogPostStatusRoute = blogPostStatusRoute;
        this.apiClient = apiClient;
        this.utility = new common_1.Utility();
    }
    Object.defineProperty(BlogPostStatusClient.prototype, "routeDefinition", {
        /**
         * Provides direct access to `blogPostStatusRoute`.
         * @method
         * @example blogPostStatusClient.routeDefinition.get();
         **/
        get: function () {
            return this.blogPostStatusRoute;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a promise that is resolved once the find action has been performed. Success response returns a list of blog post statuses resources matching the given criteria.
     * @method
     * @param options A promise that is resolved once the find action has been performed.
     * @returns A promise that is resolved once the find action has been performed.
     * @example blogPostStatusClient.find({
                    pageNumber : 1,
                    pageSize : 10,
                    orderBy : '<field>',
                    orderDirection : '<asc|desc>',
                    search : '<search-phrase>'
                })
                .then(function (collection) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                     // perform error handling here
                });
     **/
    BlogPostStatusClient.prototype.find = function (options) {
        return this.apiClient.get(this.routeDefinition.find(options));
    };
    /**
     * Returns a promise that is resolved once the get action has been performed. Success response returns a single article resource.
     * @method
     * @param id Blog slug or id which uniquely identifies article resource that needs to be retrieved.
     * @param options Options object that contains embed items.
     * @returns a promise that is resolved once the get action has been performed.
     * @example articleClient.get('<article-id>')
                    .then(function (data) {
                        // perform success action here
                    },
                     function (response, status, headers, config) {
                         // perform error handling here
                    });
     **/
    BlogPostStatusClient.prototype.get = function (id, options) {
        return this.apiClient.get(this.routeDefinition.get(id, options));
    };
    /**
     * Returns a promise that is resolved once the create blog post status action has been performed, this action creates a new blog post status resource.
     * @method
     * @param data An blog post status object that needs to be inserted into the system.
     * @returns a promise that is resolved once the create blog post status action has been performed.
     * @example blogPostStatusClient.create({
                    name : '<name>',
                    abrv : '<abrv>',
                })
                .then(function (data) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                     // perform error handling here
                });
     **/
    BlogPostStatusClient.prototype.create = function (data) {
        return this.apiClient.post(this.routeDefinition.create(), this.routeDefinition.createParams(data));
    };
    /**
     * Returns a promise that is resolved once the update blog post status action has been performed; this action updates an blog post status resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `blogPostStatusRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(blogPostStatus);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data An blogPostStatus object that needs to be updated into the system.
     * @returns A promise that is resolved once the update blogPostStatus action has been performed.
     * @example // blogPostStatus is a resource previously fetched using get action.
                    blogPostStatus.name = '<name>';
                    blogPostStatusClient.update(blogPostStatus)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    BlogPostStatusClient.prototype.update = function (data) {
        return this.apiClient.put(this.routeDefinition.update(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the remove blog post status action has been performed. If the action is successfully completed, the blogPostStatus resource will be permanently removed from the system. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `blogPostStatusRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(article);
     * let uri = params['model'].links('delete').href;
     * ```
     * @method
     * @param data An blogPostStatus object that needs to be removed from the system.
     * @returns A promise that is resolved once the remove blogPostStatus action has been performed.
     * @example // blogPostStatus is a resource previously fetched using get action.
                    blogPostStatusClient.remove(blogPostStatus)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    BlogPostStatusClient.prototype.remove = function (data) {
        return this.apiClient.delete(this.routeDefinition.delete(data));
    };
    /**
     * Returns a promise that is resolved once the purge blogPostStatus action has been performed. Please note that all blogPostStatus resources will be deleted from the system once the action is successfully completed and therefore it can only be executed by user assigned to account owner role.
     * @method
     * @example blogPostStatusClient.purge()
                    .then(function (data) {
                        // perform success action here
                    },
                     function (response, status, headers, config) {
                         // perform error handling here
                    });
     **/
    BlogPostStatusClient.prototype.purge = function () {
        return this.apiClient.delete(this.routeDefinition.purge());
    };
    var _a, _b;
    BlogPostStatusClient = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(_1.TYPES.BlogPostStatusRoute)),
        tslib_1.__param(1, inversify_1.inject(httpApi_1.httpTYPES.ApiClient)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof _1.BlogPostStatusRoute !== "undefined" && _1.BlogPostStatusRoute) === "function" ? _a : Object, typeof (_b = typeof httpApi_1.ApiClient !== "undefined" && httpApi_1.ApiClient) === "function" ? _b : Object])
    ], BlogPostStatusClient);
    return BlogPostStatusClient;
}());
exports.BlogPostStatusClient = BlogPostStatusClient;
/**
 * @overview
 ***Notes:**
 - Refer to the [REST API documentation](https://github.com/Baasic/baasic-rest-api/wiki) for detailed information about available Baasic REST API end-points.
 - All end-point objects are transformed by the associated route service.
 */ 
